import { AuthService } from '../services/AuthService';
import {
  IAccountData,
  type IFetchAccountDataRoot
} from '@/types/IFetchAccountData';
import { type IAccount } from '@/types/IAccountsResponse';
import {
  CustomError,
  getErrorData,
  type IErrorData,
  type IGenericResponseRoot
} from '../utils/getErrorData';
import { api } from '../utils/api';

export const getAccountById = async (
  id: number
): Promise<IFetchAccountDataRoot | undefined> => {
  try {
    if (AuthService.authToken === null) {
      throw new Error('Пользователь не авторизирован');
    }

    const headers = {
      Authorization: `Bearer ${AuthService.authToken}`
    };

    const response = await api.get<IFetchAccountDataRoot>(
      `/admin/account/${id}`,
      { headers }
    );

    return response.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
};

export interface IGetAdminListParams {
  limit?: number;
  offset?: number;
  account_phone?: string;
  account_id?: number;
  account_uuid?: string;
  account_name?: string;
  account_surname?: string;
  account_patronymic?: string;
  account_email?: string;
  account_role?: string;
}

export interface IGetAdminListResponse {
  items: IAccount[];
}

export const getAdminList = async (
  params?: IGetAdminListParams
): Promise<IGenericResponseRoot<IGetAdminListResponse> | IErrorData> => {
  try {
    const headers: Record<string, string> = {};

    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
    } else {
      void AuthService.checkUser();
      throw new Error('Пользователь не авторизирован');
    }

    const response = await api.get<IGenericResponseRoot<IGetAdminListResponse>>(
      `${import.meta.env.VITE_API_URL}/api/admin/accounts`,
      {
        headers,
        params
      }
    );

    if (response.status === 500) {
      throw new CustomError(
        'При получении списка пользователей произошла необработанная ошибка'
      );
    }

    if (response.data.response == null) {
      throw new CustomError('Ошибка. Пользователи не были найдены.');
    }

    return response.data;
  } catch (error) {
    const errorData = getErrorData(error);
    return errorData;
  }
};

export const updateAccountPhone = async (
  accountUuid: string,
  newPhone: string,
  signal?: AbortSignal
): Promise<IGenericResponseRoot<IAccountData>> => {
  try {
    const url = `/admin/account/${accountUuid}/phone`;
    const headers: Record<string, string> = {};
    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
    } else {
      throw new Error('Пользователь не авторизирован');
    }

    // Добавляем тело запроса
    const requestBody = {
      phone: newPhone
    };

    const response = await api.put<IGenericResponseRoot<IAccountData>>(
      url,
      requestBody,
      {
        headers,
        signal
      }
    );

    if (response.status === 500) {
      throw new CustomError(
        'Произошла необработанная ошибка при обновлении номера телефона',
        500
      );
    }

    if (response.data?.response == null) {
      throw new CustomError('Не удалось обновить номер телефона');
    }

    return response.data;
  } catch (error) {
    const errorData = getErrorData(error);
    // eslint-disable-next-line no-console
    console.error(
      'При обновлении номера телефона произошла ошибка:',
      errorData
    );
    throw error;
  }
};
